// store/footerPagesContentSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  aboutUsContentApi,
  askQuestionApi,
  faqsContentApi,
  privacyPolicyContentApi,
  refundPoliciesContentApi,
  returnPoliciesContentApi,
  termsAndConditionsContentApi,
  TestimonialDetails,
} from "../../services/footerPagesServices/footerPagesContentServices";

// Thunk for validating a coupon
export const privacyPolicyContent = createAsyncThunk(
  "footerPages/privacyPolicyContent",
  async (api_token, { rejectWithValue }) => {
    try {
      return await privacyPolicyContentApi(api_token);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Thunk for validating a coupon
export const termsAndConditionsContent = createAsyncThunk(
  "footerPages/termsAndConditionsContent",
  async ({ api_token, lang }, { rejectWithValue }) => {
    try {
      return await termsAndConditionsContentApi(api_token, lang);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Thunk for validating a coupon
export const refundPoliciesContent = createAsyncThunk(
  "footerPages/refundPoliciesContent",
  async ({ api_token, lang }, { rejectWithValue }) => {
    try {
      return await refundPoliciesContentApi(api_token, lang);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Thunk for validating a coupon
export const returnPoliciesContent = createAsyncThunk(
  "footerPages/returnPoliciesContent",
  async ({ api_token, lang }, { rejectWithValue }) => {
    try {
      return await returnPoliciesContentApi(api_token, lang);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Thunk for validating a coupon
export const fetchFaqsContent = createAsyncThunk(
  "footerPages/fetchFaqsContent",
  async ({ api_token, lang }, { rejectWithValue }) => {
    try {
      const response = await faqsContentApi(api_token, lang);
      return response.data; // API response
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk for validating a coupon
export const aboutUsContent = createAsyncThunk(
  "footerPages/aboutUsContent",
  async ({ api_token, lang }, { rejectWithValue }) => {
    try {
      return await aboutUsContentApi(api_token, lang);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitQuestion = createAsyncThunk(
  "footerPages/askQuestion",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await askQuestionApi(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchTestimonialDetails = createAsyncThunk(
  "footerPages/fetchTestimonialDetails",
  async ({ api_token, lang }, { rejectWithValue }) => {
    try {
      return await TestimonialDetails({ api_token, lang });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const footerPagesContentSlice = createSlice({
  name: "footerPagesContent",
  initialState: {
    privacyPolicyContent: "",
    termsAndConditionsContent: "",
    refundPoliciesContent: "",
    returnPoliciesContent: "",
    aboutUsContent: "",
    faqsContent: [],
    testimonialContent: [],
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    clearContent: (state) => {
      state.privacyPolicyContent = "";
      state.termsAndConditionsContent = "";
      state.refundPoliciesContent = "";
      state.returnPoliciesContent = "";
      state.aboutUsContent = "";
      state.faqsContent = "";
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(privacyPolicyContent.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(privacyPolicyContent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.privacyPolicyContent = action.payload;
      })
      .addCase(privacyPolicyContent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.isValid = false;
        state.discount = 0;
      });

    builder
      .addCase(termsAndConditionsContent.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(termsAndConditionsContent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.termsAndConditionsContent = action.payload;
      })
      .addCase(termsAndConditionsContent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });

    builder
      .addCase(aboutUsContent.pending, (state) => {
        state.status = "loading";
      })
      .addCase(aboutUsContent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.aboutUsContent = action.payload.data;
      })
      .addCase(aboutUsContent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });

    builder
      .addCase(refundPoliciesContent.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(refundPoliciesContent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.refundPoliciesContent = action.payload;
      })
      .addCase(refundPoliciesContent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });

    builder
      .addCase(returnPoliciesContent.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(returnPoliciesContent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.returnPoliciesContent = action.payload;
      })
      .addCase(returnPoliciesContent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });

    builder
      .addCase(fetchFaqsContent.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchFaqsContent.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.faqsContent = action.payload?.data || []; // actual array
      })
      .addCase(fetchFaqsContent.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });

    builder
      .addCase(submitQuestion.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(submitQuestion.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(submitQuestion.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });

    builder
      .addCase(fetchTestimonialDetails.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchTestimonialDetails.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.testimonialContent = action.payload.data;
      })
      .addCase(fetchTestimonialDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearContent } = footerPagesContentSlice.actions;
export default footerPagesContentSlice.reducer;
